import {
    BreachEvent,
    PublicationType,
    RequiredAccess,
    useFeature,
} from '@news-mono/web-common'
import H from 'history'
import React from 'react'
import {
    BASE_50_DEAL_OFFERCODE,
    useUpdateBreachWallContext,
} from '../../common'
import { showBreachDiscount, showSportBreach } from '../../helpers'
import { BlackFridayBreachNotification } from './BlackFridayBreachNotification/BlackFridayBreachNotification'
import { BreachNotification } from './BreachNotification/BreachNotification'
import { SportBreachNotification } from './SportBreachNotification/SportBreachNotification'

export interface BreachNotificationWrapperProps {
    publication: PublicationType
    breachDeal: string
    breachCreativeName: string
    requiredAccess: RequiredAccess
    onEvent: (event: BreachEvent) => void
    type: BreachWallContentType
    // Optional properties that can be passed on a per-campaign basis
    contentOptions?: {
        [key: string]: any
    }
}

export type BreachWallContentType =
    | 'default'
    | 'sport'
    | 'black-friday'
    | '50-off-breach-discount-govan50' // used on specific article categories
    | '50-off-breach-discount-coviddig'
    | '50-off-breach-discount' // to be used for most sales promotions

export type BreachWallOptions = {
    canShowSportBreach: boolean
    canShowBreachDiscountGovan: boolean
    canShowBreachDiscountCovid: boolean
    canShowBlackFriday: boolean
    canShowBreachDiscountGeneric: boolean
}

export function useBreachWallConfig(location: H.Location) {
    return getBreachWallContentType(location, {
        canShowSportBreach: useFeature('sport-breach-screen-variant'),
        canShowBreachDiscountGovan: useFeature(
            '50-off-breach-discount-govan50',
        ),
        canShowBreachDiscountCovid: useFeature(
            '50-off-breach-discount-coviddig',
        ),
        canShowBlackFriday: useFeature('black-friday'),
        canShowBreachDiscountGeneric: useFeature('50-off-breach-discount'),
    })
}

export function getBreachWallContentType(
    location: H.Location,
    options: BreachWallOptions,
): {
    deal: string
    creativeName: string
    type: BreachWallContentType
} {
    if (options.canShowBlackFriday) {
        return {
            creativeName: 'TW_Article_Breach_Black_Friday_1',
            deal: 'BLKFRI8',
            type: 'black-friday',
        }
    }

    if (showBreachDiscount(location, options.canShowBreachDiscountGovan)) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'GOVAN50',
            type: '50-off-breach-discount-govan50',
        }
    }

    if (options.canShowBreachDiscountCovid) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: 'COVIDDIG',
            type: '50-off-breach-discount-coviddig',
        }
    }

    if (options.canShowBreachDiscountGeneric) {
        return {
            creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
            deal: BASE_50_DEAL_OFFERCODE,
            type: '50-off-breach-discount',
        }
    }

    if (showSportBreach(location, options.canShowSportBreach)) {
        return {
            creativeName: 'TW_Article_Breach_Article_SPORT_PACKAGE',
            deal: 'sportspack',
            type: 'sport',
        }
    }

    return {
        creativeName: 'TW_Article_Breach_WestBlue_Design_PhotoPackages',
        deal: 'default',
        type: 'default',
    }
}

export const BreachNotificationWrapper: React.FC<BreachNotificationWrapperProps> =
    ({
        publication,
        breachDeal,
        breachCreativeName,
        requiredAccess,
        onEvent,
        type,
        contentOptions,
    }) => {
        const showLiveSportTrailer =
            contentOptions && contentOptions.showLiveSportHubTrailer

        useUpdateBreachWallContext({
            publication,
            breachCreativeName,
            breachDeal,
            requiredAccessLevel: requiredAccess.level,
        })

        switch (type) {
            case 'sport':
                return (
                    <SportBreachNotification
                        breachDeal={breachDeal}
                        breachCreativeName={breachCreativeName}
                        onEvent={onEvent}
                    />
                )
            case 'black-friday':
                return (
                    <BlackFridayBreachNotification
                        breachDeal={breachDeal}
                        breachCreativeName={breachCreativeName}
                        onEvent={onEvent}
                    />
                )
            default:
                return (
                    <BreachNotification
                        breachDeal={breachDeal}
                        breachCreativeName={breachCreativeName}
                        onEvent={onEvent}
                        type={type}
                        contentOptions={{
                            showLiveSportTrailer,
                        }}
                    />
                )
        }
    }

BreachNotificationWrapper.displayName = 'BreachNotificationWrapper'

export default BreachNotificationWrapper
